import { useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { Check, Eye, EyeOff, Search } from "lucide-react";
import { cn } from "@/lib/utils";

type Variant = "default" | "search" | "password";

export interface FloatInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
  label: string;
  error?: string;
  hint?: string;
  success?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  variant?: Variant;
  showCounter?: boolean;
}

/** Airbnb-style floating-label input — purple ring on focus, red on error. */
export function FloatInput({
  label, error, hint, success, prefix, suffix, variant = "default", showCounter,
  className, defaultValue = "", value, onChange, disabled, maxLength, type, ...rest
}: FloatInputProps) {
  const [internal, setInternal] = useState(String(defaultValue));
  const [focused, setFocused] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const val = value !== undefined ? String(value) : internal;
  const up = focused || val.length > 0;

  const isSearch = variant === "search";
  const isPassword = variant === "password";
  const effPrefix = prefix ?? (isSearch ? <Search className="w-4 h-4" /> : null);
  const effType = isPassword ? (showPwd ? "text" : "password") : type;
  const effSuffix = suffix ?? (success ? <Check className="w-4 h-4 text-success" /> : null);

  return (
    <div className={cn("relative w-full", (error || hint || showCounter) && "pb-5", className)}>
      <input
        {...rest}
        type={effType}
        disabled={disabled}
        maxLength={maxLength}
        value={val}
        onChange={(e) => { setInternal(e.target.value); onChange?.(e); }}
        onFocus={(e) => { setFocused(true); rest.onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); rest.onBlur?.(e); }}
        placeholder=" "
        className={cn(
          "w-full h-14 pt-5 pb-1.5 bg-card text-foreground font-body font-light text-sm outline-none transition-all duration-200 border-[0.5px]",
          isSearch ? "rounded-full" : "rounded-md",
          effPrefix ? "pl-11" : "pl-4",
          effSuffix || isPassword ? "pr-11" : "pr-4",
          disabled && "opacity-45 cursor-not-allowed bg-foreground/[0.03]",
          error
            ? "border-destructive/45 focus:ring-2 focus:ring-destructive/10"
            : success
            ? "border-success/50"
            : focused
            ? "border-primary ring-[3px] ring-primary/[0.08]"
            : "border-foreground/15"
        )}
      />
      {effPrefix && (
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground/70 pointer-events-none [&>svg]:w-4 [&>svg]:h-4">
          {effPrefix}
        </span>
      )}
      {(effSuffix || isPassword) && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/70 flex items-center">
          {isPassword ? (
            <button type="button" onClick={() => setShowPwd((s) => !s)} className="p-1 hover:text-foreground transition-colors" aria-label={showPwd ? "Hide" : "Show"}>
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          ) : effSuffix}
        </span>
      )}
      <label
        className={cn(
          "absolute pointer-events-none ds-floating-label font-body",
          effPrefix ? "left-11" : "left-4",
          up
            ? "right-3 truncate top-2.5 text-[10px] font-medium uppercase tracking-wider " + (error ? "text-destructive" : success ? "text-success" : "text-primary")
            : "top-1/2 -translate-y-1/2 text-sm font-light text-foreground/70"
        )}
      >
        {label}
      </label>
      {(error || hint) && (
        <div className={cn("absolute bottom-0 left-1 text-[11px] font-light font-body", error ? "text-destructive" : "text-foreground/70")}>
          {error || hint}
        </div>
      )}
      {showCounter && maxLength && (
        <div className="absolute bottom-0 right-1 text-[10px] font-light text-foreground/70 font-body">{val.length}/{maxLength}</div>
      )}
    </div>
  );
}

/** Textarea variant of FloatInput */
export interface FloatTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "prefix"> {
  label: string;
  error?: string;
  hint?: string;
  showCounter?: boolean;
}
export function FloatTextarea({
  label, error, hint, showCounter, className, defaultValue = "", value, onChange, disabled, maxLength, ...rest
}: FloatTextareaProps) {
  const [internal, setInternal] = useState(String(defaultValue));
  const [focused, setFocused] = useState(false);
  const val = value !== undefined ? String(value) : internal;
  const up = focused || val.length > 0;
  return (
    <div className={cn("relative w-full", (error || hint || showCounter) && "pb-5", className)}>
      <textarea
        {...rest}
        disabled={disabled}
        maxLength={maxLength}
        value={val}
        onChange={(e) => { setInternal(e.target.value); onChange?.(e); }}
        onFocus={(e) => { setFocused(true); rest.onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); rest.onBlur?.(e); }}
        placeholder=" "
        className={cn(
          "w-full min-h-[100px] resize-y px-4 pt-6 pb-2 bg-card text-foreground font-body font-light text-sm rounded-md outline-none transition-all duration-200 border-[0.5px]",
          disabled && "opacity-45 cursor-not-allowed bg-foreground/[0.03]",
          error ? "border-destructive/45" : focused ? "border-primary ring-[3px] ring-primary/[0.08]" : "border-foreground/15"
        )}
      />
      <label
        className={cn(
          "absolute left-4 right-3 truncate pointer-events-none ds-floating-label font-body",
          up
            ? "top-2 text-[10px] font-medium uppercase tracking-wider " + (error ? "text-destructive" : "text-primary")
            : "top-5 text-sm font-light text-foreground/70"
        )}
      >
        {label}
      </label>
      {(error || hint) && (
        <div className={cn("absolute bottom-0 left-1 text-[11px] font-light font-body", error ? "text-destructive" : "text-foreground/70")}>
          {error || hint}
        </div>
      )}
      {showCounter && maxLength && (
        <div className="absolute bottom-0 right-1 text-[10px] font-light text-foreground/70 font-body">{val.length}/{maxLength}</div>
      )}
    </div>
  );
}
